import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

const TABS = [
  { to: '/catalog', label: 'Search', end: true },
  { to: '/catalog/glossary', label: 'Glossary', end: false },
  { to: '/catalog/enrichments', label: 'Review queue', end: false },
];

function isTabActive(tab: { to: string; end: boolean }, pathname: string): boolean {
  return tab.end ? pathname === tab.to : pathname === tab.to || pathname.startsWith(`${tab.to}/`);
}

export function CatalogLayout(): JSX.Element {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number; ready: boolean }>({
    left: 0,
    width: 0,
    ready: false,
  });

  useEffect(() => {
    const el = containerRef.current?.querySelector<HTMLElement>('[data-tab-active="true"]');
    if (el && containerRef.current) {
      const c = containerRef.current.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      setIndicator({ left: r.left - c.left, width: r.width, ready: true });
    }
  }, [location.pathname]);

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow eyebrow-dot mb-2.5">
          <span>MODULE</span>
        </div>
        <h1 className="h-display">Data Catalogue &amp; Glossary</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-500 dark:text-ink-400">
          Search the estate, browse business definitions, and govern AI enrichment.
        </p>
      </div>

      <nav
        ref={containerRef}
        className="relative inline-flex items-center gap-1 rounded-xl border border-ink-200/70 dark:border-white/10 bg-gradient-to-b from-white to-ink-50/60 dark:from-white/[0.04] dark:to-white/[0.04] p-1 shadow-soft"
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-1 bottom-1 rounded-lg bg-white dark:bg-white/10 shadow-soft ring-1 ring-ink-200/70 dark:ring-white/10 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{
            transform: `translateX(${indicator.left}px)`,
            width: indicator.width,
            opacity: indicator.ready ? 1 : 0,
          }}
        />
        {TABS.map((tab) => {
          const active = isTabActive(tab, location.pathname);
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              data-tab-active={active}
              className={`relative z-10 inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-semibold tracking-tight transition-colors duration-200 ${
                active ? 'text-ink-900 dark:text-ink-50' : 'text-ink-500 dark:text-ink-400 hover:text-ink-800'
              }`}
            >
              <span
                aria-hidden="true"
                className={`inline-block rounded-full transition-all duration-300 ${
                  active
                    ? 'h-1.5 w-1.5 bg-gradient-to-br from-brand-400 to-brand-700 shadow-[0_0_0_3px_rgba(16,185,129,0.16)]'
                    : 'h-0 w-0 bg-ink-300'
                }`}
              />
              {tab.label}
            </NavLink>
          );
        })}
      </nav>

      <Outlet />
    </div>
  );
}
